// src/hooks/useGamification.js
import { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

/**
 * Hook'as gamification duomenims: XP, streak, hearts, dienos tikslas.
 * Automatiškai atnaujina kas 5 minutės.
 */
export function useGamification() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newAchievements, setNewAchievements] = useState([]);

  const fetchProfile = useCallback(async () => {
    try {
      const data = await api.get('/api/v1/gamification/profile');
      setProfile(data);
    } catch (err) {
      // Tyliai – gali neveikti jei nėra prisijungta
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Atima širdį (kviesti kai vartotojas padaro klaidą žaidime).
   * Grąžina likusį širdžių kiekį.
   */
  const loseHeart = useCallback(async () => {
    try {
      const data = await api.post('/api/v1/gamification/hearts/lose');
      // Atnaujinti lokalų hearts kiekį
      setProfile(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          hearts: data.hearts ?? Math.max(0, (prev.hearts ?? 5) - 1),
          hearts_refill_in_minutes: data.refill_in_minutes ?? prev.hearts_refill_in_minutes,
        };
      });
      return data.hearts ?? 0;
    } catch (err) {
      console.error('Failed to lose heart:', err);
      // Fallback: atimti lokaliai
      setProfile(prev => {
        if (!prev) return prev;
        return { ...prev, hearts: Math.max(0, (prev.hearts ?? 5) - 1) };
      });
      return null;
    }
  }, []);

  /**
   * Kviečiamas po sėkmingo žaidimo.
   * gamificationData – duomenys iš backend response["gamification"]
   */
  const handleGameResult = useCallback((gamificationData) => {
    if (!gamificationData) return;

    // Atnaujinti lokalų state
    setProfile(prev => {
      if (!prev) return prev;

      const newXp = gamificationData.total_xp ?? prev.xp;
      const newLevel = gamificationData.level ?? prev.level;

      // XP progress atnaujinimas
      const xpInLevel = gamificationData.xp_in_current_level ?? prev.xp_in_current_level;
      const xpToNext = gamificationData.xp_to_next_level ?? prev.xp_to_next_level;
      const progressPct = gamificationData.level_progress_pct
        ?? (xpToNext > 0 ? Math.min(100, Math.round(xpInLevel / xpToNext * 100)) : prev.level_progress_pct);

      // Hearts atnaujinimas
      const newHearts = gamificationData.hearts ?? prev.hearts;
      const heartsRefill = gamificationData.hearts_refill_in_minutes ?? prev.hearts_refill_in_minutes;

      // Daily progress
      const prevDailyEarned = prev.daily?.earned_xp ?? 0;
      const xpEarned = gamificationData.xp_earned ?? 0;
      const newDailyEarned = prevDailyEarned + xpEarned;
      const dailyTarget = gamificationData.daily_target_xp ?? prev.daily?.target_xp ?? 50;
      const dailyPct = Math.min(100, Math.round(newDailyEarned / dailyTarget * 100));

      return {
        ...prev,
        xp: newXp,
        level: newLevel,
        xp_in_current_level: xpInLevel,
        xp_to_next_level: xpToNext,
        level_progress_pct: progressPct,
        streak_days: gamificationData.streak?.streak_days ?? prev.streak_days,
        hearts: newHearts,
        hearts_refill_in_minutes: heartsRefill,
        daily: {
          ...prev.daily,
          earned_xp: newDailyEarned,
          target_xp: dailyTarget,
          percentage: dailyPct,
          completed: dailyPct >= 100,
        },
      };
    });

    // Nauji achievements
    if (gamificationData.new_achievements?.length) {
      setNewAchievements(gamificationData.new_achievements);
    }

    // Atnaujinti iš serverio po 1.5s (greičiau nei anksčiau)
    setTimeout(fetchProfile, 1500);
  }, [fetchProfile]);

  useEffect(() => {
    fetchProfile();
    const interval = setInterval(fetchProfile, 5 * 60 * 1000); // kas 5 min
    
    // Klausytis globalių žaidimo pabaigos atnaujinimų
    const handleUpdate = (e) => {
      if (e.detail) {
        handleGameResult(e.detail);
      }
    };
    window.addEventListener('gamification-update', handleUpdate);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('gamification-update', handleUpdate);
    };
  }, [fetchProfile, handleGameResult]);

  const clearNewAchievements = useCallback(() => {
    setNewAchievements([]);
  }, []);

  return {
    profile,
    loading,
    newAchievements,
    handleGameResult,
    loseHeart,
    clearNewAchievements,
    refresh: fetchProfile,
  };
}

